// /en/mfa-legalisation/documents — Wave 63: EN hub of the six MFA legalisation document families
import { createFileRoute, Link } from "@tanstack/react-router";
import ServiceAuthorityBlock from "@/components/ServiceAuthorityBlock";
import { APOSTILLE_STATUS } from "@/data/apostille-status";
import { ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const PATH = "/en/mfa-legalisation/documents";
const URL = `${SITE}${PATH}`;
const TH_URL = `${SITE}/mfa-legalisation/documents`;
const TITLE = "Documents for MFA Legalisation in Thailand — 6 Families | NYC";
const DESC =
  "Every document family that passes through the Department of Consular Affairs (MFA) before the destination embassy: order of steps, preparation checklists and what officers actually verify.";

export const Route = createFileRoute("/en/mfa-legalisation/documents/")({
  loader: async () => {
    const { MFA_DOC_GROUPS } = await import("@/data/mfa-doc-groups");
    return {
      __d: { MFA_DOC_GROUPS },
      groups: MFA_DOC_GROUPS.map((g) => ({
        slug: g.slug,
        title: g.titleEn,
        faqs: g.faqsEn.map((f) => ({ q: f.q, a: f.a })),
      })),
    };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: ctaTitle(TITLE, PATH, "en") },
      { name: "description", content: ctaDesc(DESC, PATH, "en") },
      { property: "og:title", content: ctaTitle(TITLE, PATH, "en") },
      { property: "og:description", content: ctaDesc(DESC, PATH, "en") },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "en", href: URL },
      { rel: "alternate", hrefLang: "th-TH", href: TH_URL },
      { rel: "alternate", hrefLang: "x-default", href: TH_URL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          inLanguage: "en",
          mainEntity: (loaderData?.groups ?? []).flatMap((g) =>
            g.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          ),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "@id": `${URL}#groups`,
          name: "Document families requiring MFA consular legalisation",
          numberOfItems: (loaderData?.groups ?? []).length,
          itemListElement: (loaderData?.groups ?? []).map((g, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: g.title,
            url: `${URL}/${g.slug}`,
          })),
        }),
      },
    ],
  }),
  component: MfaDocumentsEnPage,
});

function MfaDocumentsEnPage() {
  const { __d: { MFA_DOC_GROUPS } } = Route.useLoaderData() as { __d: typeof import("@/data/mfa-doc-groups") };
  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <nav className="mb-4 text-sm text-muted-foreground">
        <Link to="/en/mfa-legalisation" className="hover:underline">
          MFA + Embassy Legalisation
        </Link>{" "}
        / Documents we legalise
      </nav>

      <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
        Documents for MFA Legalisation in Thailand — 6 Families
      </h1>

      <p className="mt-4 text-base leading-8 text-muted-foreground" data-speakable>
        Legalisation at the Department of Consular Affairs, Ministry of Foreign Affairs, certifies the signature and
        seal of the officer who issued the document — not the facts written inside it. Government-issued records can be
        filed directly, while private documents must first carry a signature certification by a Notarial Services
        Attorney registered with the Lawyers Council of Thailand. This hub groups documents by use case, with the order
        of steps and a preparation checklist for each family.
      </p>

      <aside className="mt-6 rounded-lg border border-border bg-muted/40 p-4 text-sm leading-7">
        <strong>Thailand's Apostille status:</strong> Thailand deposited its instrument of accession on{" "}
        {APOSTILLE_STATUS.accessionDepositEn}, and the Convention enters into force for Thailand on{" "}
        {APOSTILLE_STATUS.entryIntoForceEn}. Until then, documents still pass through the Department of Consular Affairs
        and the destination embassy.{" "}
        <a href={APOSTILLE_STATUS.sourceUrl} target="_blank" rel="noopener noreferrer nofollow" className="underline">
          Source: HCCH
        </a>
      </aside>

      <ul className="mt-8 grid gap-2 sm:grid-cols-2">
        {MFA_DOC_GROUPS.map((g) => (
          <li key={g.slug}>
            <Link
              to="/en/mfa-legalisation/documents/$group"
              params={{ group: g.slug }}
              className="text-primary hover:underline"
            >
              {g.titleEn}
            </Link>
          </li>
        ))}
      </ul>

      {MFA_DOC_GROUPS.map((g) => (
        <section key={g.slug} id={g.slug} className="mt-12 scroll-mt-24">
          <h2 className="text-2xl font-semibold">{g.titleEn}</h2>
          <p className="mt-2 text-muted-foreground">{g.summaryEn}</p>
          <div className="mt-4 space-y-4 leading-8">
            {g.bodyEn.slice(0, 2).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <Link
            to="/en/mfa-legalisation/documents/$group"
            params={{ group: g.slug }}
            className="mt-3 inline-block text-primary hover:underline"
          >
            Read the full {g.titleEn} guide →
          </Link>
        </section>
      ))}
          <ServiceAuthorityBlock topic="mfa" locale="en" />
    </main>
  );
}
